// Types
import {
  AllEventsDetailsResponse,
  AllMealsDetailsResponse,
  EntrantTypesResponse,
  EventClassesResponse,
  EventContactsResponse,
  EventDetailsResponse,
  GendersResponse,
  MealPackagesResponse,
  PCAAgeRangesResponse,
  RegionsResponse,
  ResourcesResponse,
  ShirtSizesResponse,
  VehicleBodyStyleResponse,
  VehicleColorResponse,
} from '@/types/apiResponses';
import {
  EntrantTypeOptions,
  GenderOptions,
  PCAAgeRangeOptions,
  RegionOptions,
  ShirtSizeOptions,
  VehicleBodyStyleOptions,
  VehicleColorOptions,
} from '@/types/formOptions';
import { ParadeEvent, ParadeMeal, TimeSlotsOptions, TimeSlotsResponse } from 'parade_types';

// Utils
import { defineStore } from 'pinia';
import { fetchAllEventsDetails } from '@/api/apiCalls';

// Local types
type AppDataStoreState = {
  allEventsDetails: AllEventsDetailsResponse;
  allMealsDetails: AllMealsDetailsResponse;
  isLoading: boolean;
  entrantTypeOptions: EntrantTypeOptions;
  eventClasses: EventClassesResponse;
  eventContacts: EventContactsResponse;
  eventDetails: EventDetailsResponse;
  genderOptions: GenderOptions;
  mealPackages: MealPackagesResponse;
  pcaAgeRangeOptions: PCAAgeRangeOptions;
  regionOptions: RegionOptions;
  resources: { [resourceId: number]: ResourcesResponse[number] };
  shirtSizeOptions: ShirtSizeOptions;
  timeSlots: TimeSlotsOptions;
  vehicleBodyOptions: VehicleBodyStyleOptions;
  vehicleColorOptions: VehicleColorOptions;
  initialLoadComplete: boolean;
  preSelectJuniorsAtParade: boolean;
};

export const useAppDataStore = defineStore('appDataStore', {
  state: (): AppDataStoreState => ({
    allEventsDetails: [] as unknown as AllEventsDetailsResponse,
    allMealsDetails: [] as unknown as AllMealsDetailsResponse,
    isLoading: false,
    entrantTypeOptions: [] as unknown as EntrantTypeOptions,
    eventClasses: [] as unknown as EventClassesResponse,
    eventContacts: [] as unknown as EventContactsResponse,
    eventDetails: {} as EventDetailsResponse,
    genderOptions: [] as unknown as GenderOptions,
    mealPackages: [] as unknown as MealPackagesResponse,
    pcaAgeRangeOptions: [] as unknown as PCAAgeRangeOptions,
    regionOptions: [] as RegionOptions,
    resources: [] as unknown as { [resourceId: number]: ResourcesResponse[number] },
    shirtSizeOptions: [] as unknown as ShirtSizeOptions,
    timeSlots: [] as unknown as TimeSlotsOptions,
    vehicleBodyOptions: [] as unknown as VehicleBodyStyleOptions,
    vehicleColorOptions: [] as unknown as VehicleColorOptions,
    initialLoadComplete: false,
    preSelectJuniorsAtParade: false,
  }),

  getters: {
    /**
     * @name getIsInitialLoadComplete
     * @description Returns true if the initial load has completed
     */
    getIsInitialLoadComplete(): AppDataStoreState['initialLoadComplete'] {
      return this.initialLoadComplete;
    },

    /**
     * @name getIsLoading
     * @description Returns true if the app is loading
     */
    getIsLoading(): AppDataStoreState['isLoading'] {
      return this.isLoading;
    },

    /**
     * @name getIsPastLockInDate
     * @description Returns true if the lock-in date is past
     */
    getIsPastLockInDate(): boolean {
      const lateRegistrationDate: Date = new Date(this.eventDetails.ENTRANTLOCKOUTDATE);
      const currentDate: Date = new Date();
      return currentDate > lateRegistrationDate;
    },

    /**
     * @name getTimeSlotOptions
     * @description Returns the time slots data 
     */
    getTimeSlotOptions(): AppDataStoreState['timeSlots'] {
      return this.timeSlots;
    },

    /**
     * @name getAllEventsDetails
     * @description Returns all events details
     */
    getAllEventsDetails(): AppDataStoreState['allEventsDetails'] {
      return this.allEventsDetails;
    },

    /**
     * @name getAllMealsDetails
     * @description Returns all meals details
     */
    getAllMealsDetails(): AppDataStoreState['allMealsDetails'] {
      return this.allMealsDetails;
    },

    /**
     * @name getShirtSizeOptions
     * @description Returns the shirt sizes options
     */
    getShirtSizeOptions(): AppDataStoreState['shirtSizeOptions'] {
      return this.shirtSizeOptions;
    },

    /**
     * @name getEventClasses
     * @description Returns the event classes
     */
    getEventClasses(): AppDataStoreState['eventClasses'] {
      return this.eventClasses;
    },

    /**
     * @name getEventContacts
     * @description Returns the event contacts data
     */
    getEventContacts(): AppDataStoreState['eventContacts'] {
      return this.eventContacts
    },

    /**
     * @name getResources
     * @returns Returns the resources data
     */
    getResources(): AppDataStoreState['resources'] {
      return this.resources;
    },

    /**
     * @name
     * @description Returns the preSelectJuniorsAtParade flag
     */
    getPreSelectJuniorsAtParade(): AppDataStoreState['preSelectJuniorsAtParade'] {
      return this.preSelectJuniorsAtParade;
    },

    /**
     * @name isChildEntrantType
     * @description Returns true if the entrant type is a child entrant type (i.e. Family, JPP, CAFP, Ages 1-12, or Ages 13-17)
     * @param entrantTypeId: number
     */
    isChildEntrantType(): (entrantTypeId: number) => boolean {
      return (entrantTypeId: number) => entrantTypeId === 4 || entrantTypeId === 5 || entrantTypeId === 6 || entrantTypeId === 7 || entrantTypeId === 8;
    },

    /**
     * @name isAdultEntrantType
     * @description Returns true if the entrant type is an adult entrant type (i.e. Entrant, Co-Entrant, or Guest)
     * @param entrantTypeId: number
     */
    isAdultEntrantType(): (entrantTypeId: number) => boolean {
      return (entrantTypeId: number) => entrantTypeId === 1 || entrantTypeId === 2 || entrantTypeId === 3;
    },

    /**
     * @name isChildEntrantTypeForMeals
     * @description Returns true if the entrant type is a child entrant type (i.e. Ages 1-12)
     * @param entrantTypeId: number
     */
    isChildEntrantTypeForMeals(): (entrantTypeId: number) => boolean {
      return (entrantTypeId: number) => entrantTypeId === 8;
    },

    /**
     * @name isAdultEntrantTypeForMeals
     * @description Returns true if the entrant type is an adult entrant type (i.e. Entrant, Co-Entrant, Guest, JPP, CAFP, or Ages 13-17)
     * @param entrantTypeId: number
     */
    isAdultEntrantTypeForMeals(): (entrantTypeId: number) => boolean {
      return (entrantTypeId: number) => entrantTypeId === 1 || entrantTypeId === 2 || entrantTypeId === 3 || entrantTypeId === 5 || entrantTypeId === 6 || entrantTypeId === 7;
    },

    /**
     * @name isCarEntrantType
     * @description Returns true if the entrant type is a car entrant type
     * @param entrantTypeId: number
     */
    isCarEntrantType(): (entrantTypeId: number) => boolean {
      return (entrantTypeId: number) => entrantTypeId === 9;
    },

    /**
     * @name getEventDetailsById
     * @description Returns the event details by id
     */
    getEventDetailsById(): (id: number) => ParadeEvent {
      return (id: number) => {
        const event: ParadeEvent | undefined = this.allEventsDetails.find((event: ParadeEvent) => event.EVENT_ID === id);
        if (event === undefined) {
          console.log('undefined event id: ', id);
          throw new Error('getEventDetailsById: event is undefined');
        }
        return event;
        // if (event.EVENT_ID === 175) {
        //   event.CUSTOM_INPUTS = [
        //     {
        //       ID: 123,
        //       PROMPT: 'Custom Text Input Question',
        //       LABEL: 'Custom Label',
        //       MAX_LENGTH: 5,
        //       // @ts-ignore
        //       TYPE: 'TEXT_INPUT',
        //       REQUIRED: 0,
        //     },
        //     {
        //       ID: 456,
        //       PROMPT: 'Custom Yes or No Question',
        //       // @ts-ignore
        //       TYPE: 'YES_NO',
        //       REQUIRED: 1,
        //     },
        //     {
        //       ID: 789,
        //       PROMPT: 'Custom Radio Button Question',
        //       // @ts-ignore
        //       TYPE: 'RADIO',
        //       OPTIONS: [
        //         'Option 1',
        //         'Option 2',
        //         'Option 3',
        //         'Option 4',
        //       ],
        //       REQUIRED: 1,
        //     },
        //   ];
        // }
        // return event;
      }
    },

    /**
     * @name getMealDetailsById
     * @description Returns the meal details by id
     */
    getMealDetailsById(): (id: number) => ParadeMeal {
      return (id: number) => {
        const meal: ParadeMeal | undefined = this.allMealsDetails.find((meal: ParadeMeal) => meal.ID === id);
        if (meal === undefined) {
          throw new Error('getMealDetailsById: meal is undefined');
        }
        return meal;
      }
    },

    /**
     * @name getPCAAgeRangeOptions
     * @description Returns the age ranges options
     */
    getPCAAgeRangeOptions(): AppDataStoreState['pcaAgeRangeOptions'] {
      return this.pcaAgeRangeOptions;
    },

    /**
     * @name getEntrantTypeOptions
     * @description Returns the entrant types options
     */
    getEntrantTypeOptions(): AppDataStoreState['entrantTypeOptions'] {
      return this.entrantTypeOptions;
    },

    /**
     * @name getGenderOptions
     * @description Returns the genders options
     */
    getGenderOptions(): AppDataStoreState['genderOptions'] {
      return this.genderOptions;
    },

    /**
     * @name getRegionOptions
     * @description Returns the regions options
     */
    getRegionOptions(): AppDataStoreState['regionOptions'] {
      return this.regionOptions;
    },

    /**
     * @name getEventDetails
     * @description Returns the event details
     */
    getEventDetails(): AppDataStoreState['eventDetails'] {
      return this.eventDetails;
    },

    /**
     * @name getVehicleBodyOptions
     * @description Returns the vehicle body options
     */
    getVehicleBodyOptions(): AppDataStoreState['vehicleBodyOptions'] {
      return this.vehicleBodyOptions;
    },

    /**
     * @name getVehicleColorOptions
     * @description Returns the vehicle color options
     */
    getVehicleColorOptions(): AppDataStoreState['vehicleColorOptions'] {
      return this.vehicleColorOptions;
    },

    /** This was used for prices when we were getting the data from getEventSettings endpoint, but now
     *  we're getting the data for each item individually in the getEntireParty endpoint.
     * @name getRegistrationPrices
     * @description Returns the prices for different registration types
     */
  //   getRegistrationPrices(): {
  //     adult: number;
  //     base: number;
  //     cafp: number;
  //     coEntrant: number;
  //     jpp: number;
  //     lateFee: number;
  //     preteen: number;
  //     teen: number;
  //   } {
  //     return {
  //       adult: Number(this.eventDetails.ADULT_COST),
  //       base: Number(this.eventDetails.BASE_COST),
  //       cafp: Number(this.eventDetails.TEEN_COST),
  //       coEntrant: Number(this.eventDetails.CO_ENTRANT_COST),
  //       jpp: Number(this.eventDetails.JPP_COST),
  //       lateFee: Number(this.eventDetails.LATE_FEE),
  //       preteen: Number(this.eventDetails.PRETEEN_COST),
  //       teen: Number(this.eventDetails.TEEN_COST),
  //     };
  //   },
  },

  actions: {
    /**
     * @name setInitData
     * @description Takes data form API responses and reformats it to fit the options data structure we need on the FE
     */
    setInitData(data: {
      allEventsDetails: AllEventsDetailsResponse | null;
      allMealsDetails: AllMealsDetailsResponse | null;
      entrantTypes: EntrantTypesResponse | null;
      eventClasses: EventClassesResponse | null;
      eventContacts: EventContactsResponse | null;
      eventDetails: EventDetailsResponse | null;
      genders: GendersResponse | null;
      mealPackages: MealPackagesResponse | null;
      pcaAgeRanges: PCAAgeRangesResponse | null;
      regions: RegionsResponse | null;
      resources: ResourcesResponse | null;
      shirtSizes: ShirtSizesResponse | null;
      timeSlots: TimeSlotsResponse | null;
      vehicleBodyStyles: VehicleBodyStyleResponse | null;
      vehicleColors: VehicleColorResponse | null;
    }): void {
      if (data.pcaAgeRanges !== null) {
        this.pcaAgeRangeOptions = data.pcaAgeRanges.map(
          (pcaAgeRange: PCAAgeRangesResponse[number]) => {
            return {
              title:
                pcaAgeRange.AGE_RANGE_ID === 1
                  ? 'Adult'
                  : pcaAgeRange.AGE_RANGE_ID === 2
                  ? 'College Age (CAFP 18-25)'
                  : pcaAgeRange.AGE_RANGE_ID === 3
                  ? 'Junior (JPP 13-17)'
                  : pcaAgeRange.AGE_RANGE_ID === 4
                  ? 'Teen (13-15)'
                  : 'Preteen (11-12)',
              value: pcaAgeRange.AGE_RANGE_ID,
            };
          }
        ) as PCAAgeRangeOptions;
      }
      if (data.entrantTypes !== null) {
        this.entrantTypeOptions = data.entrantTypes.map(
          (entrantType: EntrantTypesResponse[number]) => {
            return {
              title: entrantType.ENTRANT_TYPE_DESC,
              value: entrantType.ENTRANT_TYPE_ID
            };
          }
        ) as EntrantTypeOptions;
      }
      if (data.genders !== null) {
        this.genderOptions = data.genders.map((gender: GendersResponse[number]) => {
          return {
            title:
              gender.GENDER_ID === 1
                ? 'Male'
                : gender.GENDER_ID === 2
                ? 'Female'
                : 'Prefer not to say',
            value: gender.GENDER_ID,
          };
        }) as GenderOptions;
      }
      if (data.eventDetails !== null) {
        this.eventDetails = data.eventDetails;
      }
      if (data.regions !== null) {
        this.regionOptions = data.regions.map((region: RegionsResponse[number]) => {
          return {
            title: `${region.REGIONNAME} (${region.REGIONCODE})`,
            value: region.REGIONID,
          };
        });
      }
      if (data.vehicleBodyStyles !== null) {
        this.vehicleBodyOptions = data.vehicleBodyStyles.map(
          (item: VehicleBodyStyleResponse[number]) => {
            return {
              title: item.BODYSTYLENAME,
              value: item.BODYSTYLEID,
            };
          }
        ) as VehicleBodyStyleOptions;
      }
      if (data.vehicleColors !== null) {
        this.vehicleColorOptions = data.vehicleColors.map((item: VehicleColorResponse[number]) => {
          return {
            title: item.COLORNAME,
            value: item.COLORID,
          };
        }) as VehicleColorOptions;
      }
      if (data.allEventsDetails !== null) {    
        this.allEventsDetails = data.allEventsDetails;
      }
      if (data.shirtSizes !== null) {
        this.shirtSizeOptions = data.shirtSizes.map((item: ShirtSizesResponse[number]) => {
          return {
            title: item.NAME,
            value: item.ID,
          };
        }) as ShirtSizeOptions;
      }
      if (data.eventClasses !== null) {
        this.eventClasses = data.eventClasses;
      }
      if (data.eventContacts !== null) {
        this.eventContacts = data.eventContacts;
      }
      if (data.allMealsDetails !== null) {
        this.allMealsDetails = data.allMealsDetails.filter((meal: ParadeMeal) => meal.STATUS === 1); // Ignore status 0 meaning the meal is not available
      }
      if (data.mealPackages !== null) {
        this.mealPackages = data.mealPackages;
      }
      if (data.timeSlots !== null) {
        this.timeSlots = data.timeSlots.map((timeSlot: TimeSlotsResponse[number]) => {
          return {
            title: timeSlot.TIMESLOT,
            value: timeSlot.TIMESLOTID,
          };
        });
      }
      if (data.resources !== null) {
        this.resources = data.resources.reduce((acc: { [resourceId: number]: ResourcesResponse[number] }, resourceItem: ResourcesResponse[number]) => {
          acc[resourceItem.RESOURCEID] = resourceItem
          return acc;
        }, {});
      }
    },

    setPreSelectJuniorsAtParade(val: boolean): void {
      this.preSelectJuniorsAtParade = val;
    },

    setIsLoading(isLoading: boolean): void {
      this.isLoading = isLoading;
    },

    setInitialLoadComplete(isComplete: boolean): void {
      this.initialLoadComplete = isComplete;
    },

    async requestAllEventsDetails(): Promise<void> {
      const allEventsDetailsResponse: AllEventsDetailsResponse = await fetchAllEventsDetails();
      this.allEventsDetails = allEventsDetailsResponse;
    },
  },
});
